import { useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { createParty } from "../firebaseClient";
import { RAIDS } from "../constants/battle/raids";
import { KNOWN_JOBS } from "../constants/battle/jobs";
import { fetchLoaSummary } from "../lib/loaClient";
import AppModal from "./AppModal";

function stripGate(name) {
  return String(name || "").replace(/\s*\d+\s*관문\s*$/i, "").trim();
}
const BASE_RAID_NAMES = Array.from(new Set((RAIDS || []).map((r) => stripGate(r.name))));

export default function CreatePartyModal({ onClose, onCreated }) {
  const { user: authUser } = useAuth();

  const [raidName, setRaidName] = useState(BASE_RAID_NAMES?.[0] || "");
  const [capacity, setCapacity] = useState(8);

  const [nickname, setNickname] = useState("");
  const [jobId, setJobId] = useState("");
  const [jobLabel, setJobLabel] = useState("");
  const [engraving, setEngraving] = useState("");
  const [combatPower, setCombatPower] = useState(null);
  const [level, setLevel] = useState(null);

  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const raidSelectRef = useRef(null);

  const inputCls =
    "rounded-lg border border-zinc-950/10 bg-white px-4 py-2 text-sm font-semibold " +
    "text-zinc-950 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 " +
    "dark:bg-zinc-900 dark:text-zinc-100 dark:border-white/10 dark:placeholder-zinc-500 dark:focus:ring-indigo-400";

  const selectCls =
    "appearance-none inline-block rounded-lg border border-zinc-950/10 cursor-pointer px-4 py-2 pr-8 bg-white " +
    "text-sm font-semibold text-zinc-950 focus:outline-none focus:ring-1 focus:ring-indigo-500 " +
    "bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center] dark:bg-zinc-900 dark:text-zinc-100 dark:border-white/10";

  const chevronSvg = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9CA3AF"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .92 1.18l-4.17 3.31a.75.75 0 0 1-.92 0L5.21 8.41a.75.75 0 0 1 .02-1.2z"/></svg>'
  );

  const mapClassToJobId = (className) => {
    const norm = (s) => String(s || "").replace(/\s/g, "").toLowerCase();
    const t = norm(className);
    for (const j of (KNOWN_JOBS || [])) {
      const id = typeof j === "string" ? j : (j.id || j.key || j.code || j.name || j.label);
      const label = typeof j === "string" ? j : (j.label || j.name || id);
      if (norm(id) === t || norm(label) === t) return String(id);
    }
    return String(className);
  };

  const valid =
    authUser &&
    raidName &&
    capacity > 0 &&
    nickname.trim().length > 0 &&
    jobId && engraving; // 확인 후 값 존재

  async function checkNickname() {
    const name = nickname.trim();
    if (!name) { setErr("닉네임을 입력하세요."); return; }
    setErr(""); setChecking(true);

    try {
      const info = await fetchLoaSummary(name);
      const mapped = mapClassToJobId(info.className);
      setJobId(mapped);
      setJobLabel(info.className || mapped);
      setEngraving(info.engraving || "");
      setCombatPower(info.combatPowerText ?? null);
      setLevel(info.itemAvgLevelText ?? null);

      if (!mapped || !info.engraving) {
        setErr("직업/직업각인을 확인할 수 없습니다. 닉네임을 다시 확인하세요.");
      }
    } catch (e) {
      console.error(e);
      setErr("닉네임 확인에 실패했습니다.");
      setJobId(""); setJobLabel(""); setEngraving(""); setCombatPower(null); setLevel(null);
    } finally {
      setChecking(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!valid || submitting) return;
    setErr(""); setSubmitting(true);

    try {
      const code = await createParty({
        ownerUid: authUser.uid,
        raidName,
        capacity: Number(capacity),
        nickname: nickname.trim(),
        job: jobId,
        engraving,
        combatPower, // 선택 전달
        level,
      });
      onCreated?.(code);
    } catch (e2) {
      console.error(e2);
      setErr(e2?.message || "공격대 생성 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppModal
      open={true}
      onClose={onClose}
      title="공격대 생성"
      maxWidthClass="max-w-lg"
      initialFocusRef={raidSelectRef}
      footer={
        <button
          type="submit"
          form="createPartyForm"
          className="btn btn-primary w-full disabled:opacity-60"
          disabled={!valid || submitting}
        >
          {submitting ? "생성 중..." : "공격대 생성하기"}
        </button>
      }
    >
      <form id="createPartyForm" onSubmit={handleCreate} className="flex flex-col gap-4">
        {/* 레이드 선택 + 인원수 */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">레이드 선택</label>
            <select
              ref={raidSelectRef}
              className={selectCls + " w-full"}
              style={{ backgroundImage: `url("data:image/svg+xml,${chevronSvg}")` }}
              value={raidName}
              onChange={(e) => setRaidName(e.target.value)}
              required
              title="레이드 선택"
            >
              {BASE_RAID_NAMES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-32">
            <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">인원수</label>
            <select
              className={selectCls + " w-full"}
              style={{ backgroundImage: `url("data:image/svg+xml,${chevronSvg}")` }}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value))}
              required
              title="인원수 선택"
            >
              {[4, 8, 16].map((n) => (
                <option key={n} value={n}>{n}인</option>
              ))}
            </select>
          </div>
        </div>

        {/* 닉네임 + 확인 버튼 */}
        <div>
          <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">닉네임</label>
          <div className="flex gap-2">
            <input
              className={inputCls+ " flex-1"}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={12}
              placeholder="인게임 캐릭터 닉네임을 정확히 입력해주세요."
              required
            />
            <button
              type="button"
              onClick={checkNickname}
              className="btn btn-secondary whitespace-nowrap"
              disabled={checking || !nickname.trim()}
              title="캐릭터 정보 조회"
            >
              {checking ? "확인중" : "확인하기"}
            </button>
          </div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{nickname.length}/12</div>
        </div>

        {/* 직업/직업각인 (readonly) + 전투력 hidden */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">직업</label>
            <input className={inputCls+ " w-full"} value={jobLabel} readOnly placeholder="닉네임 확인 후 자동입력" />
          </div>
          <div className="flex-1">
            <label className="block text-sm mb-1 text-zinc-600 dark:text-zinc-300">직업각인</label>
            <input className={inputCls+ " w-full"} value={engraving} readOnly placeholder="닉네임 확인 후 자동입력" />
          </div>
          <input type="hidden" name="combatPower" value={combatPower ?? ""} />
          <input type="hidden" name="level" value={level ?? ""} />
        </div>

        {!!err && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-300">
            {err}
          </div>
        )}
      </form>
    </AppModal>
  );
}
